import {createSlice, PayloadAction} from "@reduxjs/toolkit";

import {RootState} from "../../../../store/types/RootState";
import {getDefaultListSortOption, ListSortContext} from "../../../sort/constants/create_list_sort_options";
import {ListSortOption} from "../../../sort/types/ListSortOption";
import {ExternalFavoriteTrackingLocation} from "../../../tracking/amplitude/get_amplitude_external_favorite_properties";
import {closedFavoritesExternalOverlaysState, IFavoritesExternalOverlaysState, IOpenFavoriteOverlayPayload} from "../types/FavoritesExternalOverlayState";

interface IFavoritesListState {
    externalOverlays: IFavoritesExternalOverlaysState;
    isAddToComparatorModalOpen: boolean;
    isComparatorFeaturePickerOpen: boolean;
    isRemoveFromComparisonWarningModalOpen: boolean;
    isSelectionMode: boolean;
    listParams: {
        sort: ListSortOption;
    };
    selectedFavoriteIds: number[];
}

const initialState: IFavoritesListState = {
    externalOverlays: closedFavoritesExternalOverlaysState,
    isAddToComparatorModalOpen: false,
    isComparatorFeaturePickerOpen: false,
    isRemoveFromComparisonWarningModalOpen: false,
    isSelectionMode: false,
    listParams: {
        sort: getDefaultListSortOption(ListSortContext.FAVORITES)
    },
    selectedFavoriteIds: []
};

export const favoritesListSlice = createSlice({
    name: "favoritesList",
    initialState,
    reducers: {
        closeAddOfferLoadingOverlay: (state) => {
            state.externalOverlays.addOfferLoading.isOpen = false;
        },
        closeAddOfferFlow: (state) => {
            state.externalOverlays.addOfferLoading.isOpen = false;
            state.externalOverlays.addOfferSuccess = {
                favoriteId: null,
                isOpen: false,
                trackingLocation: ExternalFavoriteTrackingLocation.FAVOURITE
            };
        },
        closeAddOfferOverlay: (state) => {
            state.externalOverlays.addOffer.isOpen = false;
        },
        closeAddOfferSuccessOverlay: (state) => {
            state.externalOverlays.addOfferSuccess = {
                favoriteId: null,
                isOpen: false,
                trackingLocation: ExternalFavoriteTrackingLocation.FAVOURITE
            };
        },
        closeAddToComparatorModal: (state) => {
            state.isAddToComparatorModalOpen = false;
            state.selectedFavoriteIds = [];
        },
        closeComparatorFeaturePicker: (state) => {
            state.isComparatorFeaturePickerOpen = false;
        },
        closeRemoveFromComparisonWarningModal: (state) => {
            state.isRemoveFromComparisonWarningModalOpen = false;
        },
        closeEditExternalFavoriteOverlay: (state) => {
            state.externalOverlays.editExternal = {
                favoriteId: null,
                isOpen: false,
                trackingLocation: ExternalFavoriteTrackingLocation.FAVOURITE
            };
        },
        enterSelectionMode: (state) => {
            state.isSelectionMode = true;
        },
        exitSelectionMode: (state) => {
            state.isSelectionMode = false;
            state.selectedFavoriteIds = [];
        },
        openAddOfferLoadingOverlay: (state) => {
            state.externalOverlays.addOfferLoading.isOpen = true;
        },
        openAddOfferOverlay: (state) => {
            state.externalOverlays.addOffer.isOpen = true;
        },
        openAddOfferSuccessOverlay: (state, action: PayloadAction<IOpenFavoriteOverlayPayload>) => {
            state.externalOverlays.addOfferSuccess = {
                favoriteId: action.payload.favoriteId,
                isOpen: true,
                trackingLocation: action.payload.trackingLocation ?? ExternalFavoriteTrackingLocation.FAVOURITE
            };
        },
        openAddToComparatorModal: (state) => {
            state.isAddToComparatorModalOpen = true;
        },
        openComparatorFeaturePicker: (state) => {
            state.isComparatorFeaturePickerOpen = true;
        },
        openRemoveFromComparisonWarningModal: (state) => {
            state.isRemoveFromComparisonWarningModalOpen = true;
        },
        openEditExternalFavoriteOverlay: (state, action: PayloadAction<IOpenFavoriteOverlayPayload>) => {
            state.externalOverlays.editExternal = {
                favoriteId: action.payload.favoriteId,
                isOpen: true,
                trackingLocation: action.payload.trackingLocation ?? ExternalFavoriteTrackingLocation.FAVOURITE
            };
        },
        toggleFavoriteSelection: (state, action: PayloadAction<number>) => {
            const favoriteId = action.payload;
            const selectedFavoriteIds = state.selectedFavoriteIds;

            if (selectedFavoriteIds.includes(favoriteId)) {
                state.selectedFavoriteIds = selectedFavoriteIds.filter((id) => id !== favoriteId);
                return;
            }

            selectedFavoriteIds.push(favoriteId);
        },
        setFavoritesListParams: (state, action: PayloadAction<Partial<IFavoritesListState["listParams"]>>) => {
            state.listParams = {
                ...state.listParams,
                ...action.payload
            };
        },
        setSelectedFavoriteIds: (state, action: PayloadAction<number[]>) => {
            state.selectedFavoriteIds = action.payload;
        }
    }
});

export const selectExternalFavoritesOverlays = (state: RootState) => state.favoritesList.externalOverlays;

export const selectAddOfferLoadingOverlay = (state: RootState) => state.favoritesList.externalOverlays.addOfferLoading;

export const selectAddOfferOverlay = (state: RootState) => state.favoritesList.externalOverlays.addOffer;

export const selectAddOfferSuccessOverlay = (state: RootState) => state.favoritesList.externalOverlays.addOfferSuccess;

export const selectEditExternalFavoriteOverlay = (state: RootState) => state.favoritesList.externalOverlays.editExternal;

export const selectEditExternalFavoriteOverlayFavoriteId = (state: RootState) => state.favoritesList.externalOverlays.editExternal.favoriteId;

export const selectFavoritesListParams = (state: RootState) => state.favoritesList.listParams;

export const selectIsAddToComparatorModalOpen = (state: RootState) => state.favoritesList.isAddToComparatorModalOpen;

export const selectIsComparatorFeaturePickerOpen = (state: RootState) => state.favoritesList.isComparatorFeaturePickerOpen;

export const selectIsRemoveFromComparisonWarningModalOpen = (state: RootState) => state.favoritesList.isRemoveFromComparisonWarningModalOpen;

export const selectIsSelectionMode = (state: RootState) => state.favoritesList.isSelectionMode;

export const selectSelectedFavoriteIds = (state: RootState) => state.favoritesList.selectedFavoriteIds;

export const selectIsFavoriteSelected = (favoriteId: number) => (state: RootState) => state.favoritesList.selectedFavoriteIds.includes(favoriteId);

export const {
    closeAddOfferFlow,
    closeAddOfferLoadingOverlay,
    closeAddOfferOverlay,
    closeAddOfferSuccessOverlay,
    closeAddToComparatorModal,
    closeComparatorFeaturePicker,
    closeRemoveFromComparisonWarningModal,
    closeEditExternalFavoriteOverlay,
    enterSelectionMode,
    exitSelectionMode,
    openAddOfferLoadingOverlay,
    openAddOfferOverlay,
    openAddOfferSuccessOverlay,
    openAddToComparatorModal,
    openComparatorFeaturePicker,
    openRemoveFromComparisonWarningModal,
    openEditExternalFavoriteOverlay,
    setFavoritesListParams,
    setSelectedFavoriteIds,
    toggleFavoriteSelection
} = favoritesListSlice.actions;
import {createSlice, PayloadAction} from "@reduxjs/toolkit";

import {RootState} from "../../../../store/types/RootState";
import {getDefaultListSortOption, ListSortContext} from "../../../sort/constants/create_list_sort_options";
import {ListSortOption} from "../../../sort/types/ListSortOption";
import {ExternalFavoriteTrackingLocation} from "../../../tracking/amplitude/get_amplitude_external_favorite_properties";
import {closedFavoritesExternalOverlaysState, IFavoritesExternalOverlaysState, IOpenFavoriteOverlayPayload} from "../types/FavoritesExternalOverlayState";

interface IFavoritesListState {
    externalOverlays: IFavoritesExternalOverlaysState;
    isAddToComparatorModalOpen: boolean;
    isComparatorFeaturePickerOpen: boolean;
    isRemoveFromComparisonWarningModalOpen: boolean;
    isSelectionMode: boolean;
    listParams: {
        sort: ListSortOption;
    };
    selectedFavoriteIds: number[];
}

const initialState: IFavoritesListState = {
    externalOverlays: closedFavoritesExternalOverlaysState,
    isAddToComparatorModalOpen: false,
    isComparatorFeaturePickerOpen: false,
    isRemoveFromComparisonWarningModalOpen: false,
    isSelectionMode: false,
    listParams: {
        sort: getDefaultListSortOption(ListSortContext.FAVORITES)
    },
    selectedFavoriteIds: []
};

export const favoritesListSlice = createSlice({
    name: "favoritesList",
    initialState,
    reducers: {
        closeAddOfferLoadingOverlay: (state) => {
            state.externalOverlays.addOfferLoading.isOpen = false;
        },
        closeAddOfferFlow: (state) => {
            state.externalOverlays.addOfferLoading.isOpen = false;
            state.externalOverlays.addOfferSuccess = {
                favoriteId: null,
                isOpen: false,
                trackingLocation: ExternalFavoriteTrackingLocation.FAVOURITE
            };
        },
        closeAddOfferOverlay: (state) => {
            state.externalOverlays.addOffer.isOpen = false;
        },
        closeAddOfferSuccessOverlay: (state) => {
            state.externalOverlays.addOfferSuccess = {
                favoriteId: null,
                isOpen: false,
                trackingLocation: ExternalFavoriteTrackingLocation.FAVOURITE
            };
        },
        closeAddToComparatorModal: (state) => {
            state.isAddToComparatorModalOpen = false;
            state.selectedFavoriteIds = [];
        },
        closeComparatorFeaturePicker: (state) => {
            state.isComparatorFeaturePickerOpen = false;
        },
        closeRemoveFromComparisonWarningModal: (state) => {
            state.isRemoveFromComparisonWarningModalOpen = false;
        },
        closeEditExternalFavoriteOverlay: (state) => {
            state.externalOverlays.editExternal = {
                favoriteId: null,
                isOpen: false,
                trackingLocation: ExternalFavoriteTrackingLocation.FAVOURITE
            };
        },
        enterSelectionMode: (state) => {
            state.isSelectionMode = true;
        },
        exitSelectionMode: (state) => {
            state.isSelectionMode = false;
            state.selectedFavoriteIds = [];
        },
        openAddOfferLoadingOverlay: (state) => {
            state.externalOverlays.addOfferLoading.isOpen = true;
        },
        openAddOfferOverlay: (state) => {
            state.externalOverlays.addOffer.isOpen = true;
        },
        openAddOfferSuccessOverlay: (state, action: PayloadAction<IOpenFavoriteOverlayPayload>) => {
            state.externalOverlays.addOfferSuccess = {
                favoriteId: action.payload.favoriteId,
                isOpen: true,
                trackingLocation: action.payload.trackingLocation ?? ExternalFavoriteTrackingLocation.FAVOURITE
            };
        },
        openAddToComparatorModal: (state) => {
            state.isAddToComparatorModalOpen = true;
        },
        openComparatorFeaturePicker: (state) => {
            state.isComparatorFeaturePickerOpen = true;
        },
        openRemoveFromComparisonWarningModal: (state) => {
            state.isRemoveFromComparisonWarningModalOpen = true;
        },
        openEditExternalFavoriteOverlay: (state, action: PayloadAction<IOpenFavoriteOverlayPayload>) => {
            state.externalOverlays.editExternal = {
                favoriteId: action.payload.favoriteId,
                isOpen: true,
                trackingLocation: action.payload.trackingLocation ?? ExternalFavoriteTrackingLocation.FAVOURITE
            };
        },
        toggleFavoriteSelection: (state, action: PayloadAction<number>) => {
            const favoriteId = action.payload;
            const selectedFavoriteIds = state.selectedFavoriteIds;

            if (selectedFavoriteIds.includes(favoriteId)) {
                state.selectedFavoriteIds = selectedFavoriteIds.filter((id) => id !== favoriteId);
                return;
            }

            selectedFavoriteIds.push(favoriteId);
        },
        setFavoritesListParams: (state, action: PayloadAction<Partial<IFavoritesListState["listParams"]>>) => {
            state.listParams = {
                ...state.listParams,
                ...action.payload
            };
        },
        setSelectedFavoriteIds: (state, action: PayloadAction<number[]>) => {
            state.selectedFavoriteIds = action.payload;
        }
    }
});

export const selectExternalFavoritesOverlays = (state: RootState) => state.favoritesList.externalOverlays;

export const selectAddOfferLoadingOverlay = (state: RootState) => state.favoritesList.externalOverlays.addOfferLoading;

export const selectAddOfferOverlay = (state: RootState) => state.favoritesList.externalOverlays.addOffer;

export const selectAddOfferSuccessOverlay = (state: RootState) => state.favoritesList.externalOverlays.addOfferSuccess;

export const selectEditExternalFavoriteOverlay = (state: RootState) => state.favoritesList.externalOverlays.editExternal;

export const selectEditExternalFavoriteOverlayFavoriteId = (state: RootState) => state.favoritesList.externalOverlays.editExternal.favoriteId;

export const selectFavoritesListParams = (state: RootState) => state.favoritesList.listParams;

export const selectIsAddToComparatorModalOpen = (state: RootState) => state.favoritesList.isAddToComparatorModalOpen;

export const selectIsComparatorFeaturePickerOpen = (state: RootState) => state.favoritesList.isComparatorFeaturePickerOpen;

export const selectIsRemoveFromComparisonWarningModalOpen = (state: RootState) => state.favoritesList.isRemoveFromComparisonWarningModalOpen;

export const selectIsSelectionMode = (state: RootState) => state.favoritesList.isSelectionMode;

export const selectSelectedFavoriteIds = (state: RootState) => state.favoritesList.selectedFavoriteIds;

export const selectIsFavoriteSelected = (favoriteId: number) => (state: RootState) => state.favoritesList.selectedFavoriteIds.includes(favoriteId);

export const {
    closeAddOfferFlow,
    closeAddOfferLoadingOverlay,
    closeAddOfferOverlay,
    closeAddOfferSuccessOverlay,
    closeAddToComparatorModal,
    closeComparatorFeaturePicker,
    closeRemoveFromComparisonWarningModal,
    closeEditExternalFavoriteOverlay,
    enterSelectionMode,
    exitSelectionMode,
    openAddOfferLoadingOverlay,
    openAddOfferOverlay,
    openAddOfferSuccessOverlay,
    openAddToComparatorModal,
    openComparatorFeaturePicker,
    openRemoveFromComparisonWarningModal,
    openEditExternalFavoriteOverlay,
    setFavoritesListParams,
    setSelectedFavoriteIds,
    toggleFavoriteSelection
} = favoritesListSlice.actions;
